import { CloseIcon } from 'examkrackers-components'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import eventEmitter from 'providers/eventEmitter'
import events from 'utils/events'
import { useDispatch, useSelector } from 'react-redux'
import { getCurrentCourse } from 'modules/courses/ducks/selectors'
import * as R from 'ramda'
import { snoozeCalendarArchiveModal } from 'services/CourseService'
import { fetchCurrentCourseRoutine } from 'modules/courses/ducks/actions'
import { showToastRoutine } from 'modules/toast/ducks/actions'
import { SEVERITY } from 'utils/toast'
import { isNotNilOrEmpty } from 'utils/ramda'
import { countStudyDaysInRange } from 'utils/calendar'
import {
  DAYS_AMOUNT_FOR_REMIBDER,
  STUDY_DAYS_FOR_CALENDAR
} from './utils/calendarDaysAmount'
import { isTodayOrLater } from 'utils/date'
import { addDays, format, parseISO } from 'date-fns'
import { selectCalendarArchiveEvents } from 'modules/calendar/ducks/selectors'

export default function TooltipOnboarding() {
  const currentCourse = useSelector(getCurrentCourse)
  const archiveEvents = useSelector(selectCalendarArchiveEvents)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(false)

  const hasManualEvents = isNotNilOrEmpty(archiveEvents)
  const courseStartDate = currentCourse?.calendar_start_at
  const courseEndDate = currentCourse?.exam_at
  const coursePrioriDays = currentCourse?.prioridays
  const hasStudentsEnoughStudyDays = countStudyDaysInRange(
    courseStartDate as string,
    courseEndDate as string,
    coursePrioriDays as []
  )

  const reminderValue = R.pipe(
    R.propOr('{}', 'metadata'),
    JSON.parse,
    R.propOr(false, 'calendar_archive_tooltip')
  )(currentCourse)

  // @ts-ignore
  const canSetReminder: boolean =
    reminderValue || typeof reminderValue === 'string'
  const isSecondReminder = typeof reminderValue === 'string'
  const reminderDate = isSecondReminder
    ? format(
        addDays(parseISO(reminderValue), DAYS_AMOUNT_FOR_REMIBDER),
        'yyyy-MM-dd'
      )
    : null

  const secondReminderIsReached =
    isSecondReminder && isTodayOrLater(reminderDate)

  const firstReminderIsReached = reminderValue === true

  const checkStudyDays =
    currentCourse?.type !== 'live_course'
      ? hasStudentsEnoughStudyDays >= STUDY_DAYS_FOR_CALENDAR
      : true
  const shouldPromptOnboarding = hasManualEvents && checkStudyDays

  const isVisible =
    shouldPromptOnboarding &&
    (firstReminderIsReached || secondReminderIsReached)

  React.useEffect(() => {
    setIsOpen(isVisible)
  }, [currentCourse])

  const fetchCurrentCourse = React.useCallback(
    () => dispatch(fetchCurrentCourseRoutine()),
    [dispatch]
  )

  const showToast = useCallback(
    payload => dispatch(showToastRoutine(payload)),
    [dispatch]
  )

  const handleRemindMe = () => {
    const timestamp = format(parseISO(new Date().toISOString()), 'yyyy-MM-dd')

    snoozeCalendarArchiveModal({ snooze_until: timestamp })
      .then(() => {
        fetchCurrentCourse()
        showToast({
          key: 'calendar.tooltipOnboarding.successToast',
          severity: SEVERITY.success
        })
        setIsOpen(false)
      })
      .catch(error => {
        console.error(error)
        setIsOpen(false)
      })
  }

  const handleDontRemindMe = () => {
    snoozeCalendarArchiveModal()
      .then(() => {
        fetchCurrentCourse()
        showToast({
          key: 'calendar.tooltipOnboarding.closeToast',
          severity: SEVERITY.success
        })
        setIsOpen(false)
      })
      .catch(error => {
        console.error(error)
        setIsOpen(false)
      })
  }

  const handleClose = () => {
    if (isOpen) {
      !isSecondReminder ? handleRemindMe() : handleDontRemindMe()
    }
  }

  React.useEffect(() => {
    eventEmitter.on(events.closeTooltipCalendarOnboarding, handleClose)
    return () => {
      eventEmitter.off(events.closeTooltipCalendarOnboarding, handleClose)
    }
  }, [currentCourse, isOpen])

  return (
    <Popup open={isOpen}>
      <CloseIcon onClick={handleClose} />
      <p>{t('calendar.tooltipOnboarding.description')}</p>
      <CTA onClick={handleClose}>
        {t(
          `calendar.tooltipOnboarding.${
            !isSecondReminder ? 'ctaRemindMe' : 'ctaClose'
          }`
        )}
      </CTA>
    </Popup>
  )
}

const Popup = styled.div<{ open: boolean }>`
  display: ${({ open }) => (open ? 'flex' : 'none')};
  position: absolute;
  transform: translateX(-50%) translateY(-100%);
  left: 50%;
  color: ${({ theme }) => theme.colors.main.white};
  background-color: ${({ theme }) => theme.colors.main.text};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  min-width: 307px;
  min-height: 76px;
  border-radius: 4px;
  padding: 16px;
  z-index: ${({ theme }) => theme.zIndex.modal};

  svg {
    position: absolute;
    top: 5px;
    right: 5px;
    cursor: pointer;
  }

  p {
    font-weight: 400;
    font-size: 14px;
  }

  &::before {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 59%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid ${({ theme }) => theme.colors.main.text};
  }
`

const CTA = styled.div`
  cursor: pointer;
  text-decoration: underline;
  font-weight: 400;
  font-size: 10px;
`
